/**
 * PageVersion lifecycle hooks (issue #172).
 *
 * Mirrors the API endpoints from `api/src/pages/pages.routes.ts:80-97`:
 *
 *   - GET   /api/admin/pages/:id                                          → usePage
 *   - PATCH /api/admin/page-versions/:id                                  → useUpdatePageVersion
 *   - POST  /api/admin/page-versions/:id/publish                          → usePublishPageVersion
 *   - POST  /api/admin/page-versions/:id/unpublish                        → useUnpublishPageVersion
 *   - POST  /api/admin/pages/:pageId/versions/draft-from/:sourceVersionId → useDraftFromVersion
 *
 * Every mutation invalidates `queryKeys.pages.detail(pageId)` on success so the
 * lifecycle slots (current draft / currently published / archived) re-render
 * against the authoritative server state. The `usePage` query lives here
 * rather than on `usePages.ts` so the publish-flow surface is co-located —
 * `usePages.ts` covers the page-identity-row CRUD only (per the legacy
 * comment at the top of that file).
 *
 * 409 `VERSION_RACE` handling lives at the call site, not in the hook — see
 * `PageDetailPage`. The hook surfaces the typed `ApiClientError` and the
 * page handler renders the yellow "another publish raced you" toast then
 * triggers an explicit refetch.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ArchiveExperimentPageVersionResponse,
  PageWithVersionsResponse,
  PageVersionResponse,
  PromotePageVersionResponse,
  PublishPageVersionResponse,
  UnpublishPageVersionResponse,
  UpdatePageVersionInput,
} from "@ottopilot/shared";
import { apiGet, apiPatch, apiPost } from "../lib/api";
import type { ApiClientError } from "../lib/api";
import { queryKeys } from "./queryKeys";

/**
 * Fetch the page detail (the page identity row + its latest draft +
 * currently-published version). Disabled when `pageId` is falsy so the
 * query doesn't fire on an in-flight route mount with a missing param.
 */
export function usePage(pageId: string | undefined) {
  return useQuery<PageWithVersionsResponse, ApiClientError>({
    queryKey: queryKeys.pages.detail(pageId ?? ""),
    queryFn: () => apiGet<PageWithVersionsResponse>(`/api/admin/pages/${pageId ?? ""}`),
    enabled: Boolean(pageId),
  });
}

/**
 * `PATCH /api/admin/page-versions/:id` — update a DRAFT PageVersion's
 * `title` and/or `blocksOrderJson`. The service rejects PATCHes against
 * PUBLISHED/ARCHIVED rows; the UI hides the save button on those slots so
 * the rejection is a defence-in-depth, not the primary guard.
 *
 * Takes `pageId` at hook construction time so the success invalidation
 * targets the right detail key without the caller having to thread it
 * through the mutation `variables`.
 */
export function useUpdatePageVersion(pageId: string) {
  const qc = useQueryClient();
  return useMutation<
    PageVersionResponse,
    ApiClientError,
    { versionId: string; input: UpdatePageVersionInput }
  >({
    mutationFn: ({ versionId, input }) =>
      apiPatch<PageVersionResponse>(`/api/admin/page-versions/${versionId}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pages.detail(pageId) });
    },
  });
}

/**
 * `POST /api/admin/page-versions/:id/publish` — flip a DRAFT to PUBLISHED.
 * The previously-published peer (if any) is archived atomically by the
 * service. Invalidates the detail query so both lifecycle slots refresh.
 *
 * 409 `VERSION_RACE` is surfaced as `ApiClientError` with `code` set; the
 * page handler renders the yellow "another publish raced you" toast and
 * explicitly refetches. The hook itself does not retry — re-running a
 * publish without operator confirmation would be wrong.
 */
export function usePublishPageVersion(pageId: string) {
  const qc = useQueryClient();
  return useMutation<
    PublishPageVersionResponse,
    ApiClientError,
    { versionId: string; mode?: "primary" | "experiment" }
  >({
    // Issue #243: `mode` selects "primary" (default; DRAFT → PUBLISHED,
    // demotes prior PUBLISHED → ARCHIVED) or "experiment" (DRAFT →
    // EXPERIMENT, no demotion). Body is empty by default to preserve
    // the historical call shape.
    mutationFn: ({ versionId, mode }) =>
      apiPost<PublishPageVersionResponse>(
        `/api/admin/page-versions/${versionId}/publish`,
        mode !== undefined ? { mode } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pages.detail(pageId) });
    },
  });
}

/**
 * `POST /api/admin/page-versions/:id/promote` — flip an EXPERIMENT
 * version to PUBLISHED. The prior PUBLISHED peer (if any) is demoted
 * to ARCHIVED in the same transaction. Issue #243.
 *
 * Surfaces typed `ApiClientError` codes so the call site can render
 * targeted UI: `VERSION_NOT_EXPERIMENT` if the source slipped out of
 * EXPERIMENT between the SPA's last poll and the click, or
 * `VERSION_LOCKED_BY_EXPERIMENT` if a RUNNING experiment still
 * references the version (the operator must stop the experiment
 * first).
 */
export function usePromotePageVersion(pageId: string) {
  const qc = useQueryClient();
  return useMutation<PromotePageVersionResponse, ApiClientError, { versionId: string }>({
    mutationFn: ({ versionId }) =>
      apiPost<PromotePageVersionResponse>(`/api/admin/page-versions/${versionId}/promote`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pages.detail(pageId) });
    },
  });
}

/**
 * `POST /api/admin/page-versions/:id/archive-experiment` — flip an
 * EXPERIMENT version to ARCHIVED with no replacement. The
 * "we tested it, didn't win" action. Issue #243.
 */
export function useArchiveExperimentPageVersion(pageId: string) {
  const qc = useQueryClient();
  return useMutation<ArchiveExperimentPageVersionResponse, ApiClientError, { versionId: string }>({
    mutationFn: ({ versionId }) =>
      apiPost<ArchiveExperimentPageVersionResponse>(
        `/api/admin/page-versions/${versionId}/archive-experiment`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pages.detail(pageId) });
    },
  });
}

/**
 * `POST /api/admin/page-versions/:id/unpublish` — flip the
 * currently-PUBLISHED version to ARCHIVED with no replacement. After the
 * mutation, the page has no public-facing version until the operator
 * publishes a new draft.
 */
export function useUnpublishPageVersion(pageId: string) {
  const qc = useQueryClient();
  return useMutation<UnpublishPageVersionResponse, ApiClientError, { versionId: string }>({
    mutationFn: ({ versionId }) =>
      apiPost<UnpublishPageVersionResponse>(`/api/admin/page-versions/${versionId}/unpublish`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pages.detail(pageId) });
    },
  });
}

/**
 * `POST /api/admin/pages/:pageId/versions/draft-from/:sourceVersionId` —
 * create a new DRAFT seeded from a PUBLISHED or ARCHIVED source. Used for
 * two operator workflows:
 *
 *   - "Create new draft from this" on the currently-published row — the
 *     editable amendment path.
 *   - "Rollback" on an archived row — same endpoint, archived source.
 *
 * The service rejects DRAFT sources with 422 `VERSION_NOT_ROLLBACKABLE`.
 */
export function useDraftFromVersion(pageId: string) {
  const qc = useQueryClient();
  return useMutation<PageVersionResponse, ApiClientError, { sourceVersionId: string }>({
    mutationFn: ({ sourceVersionId }) =>
      apiPost<PageVersionResponse>(
        `/api/admin/pages/${pageId}/versions/draft-from/${sourceVersionId}`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pages.detail(pageId) });
    },
  });
}
